/**
 * FX Rates management — list, manual override, auto-fetch, month-end revaluation.
 * Phase 4 of Emerald Books.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, DownloadCloud, Plus, X, ArrowRight, RefreshCw } from 'lucide-react';
import { booksClient, getBooksTenant, errMsg } from './api';
import { toast } from '../hooks/use-toast';

const CCY_LIST = ['GBP', 'USD', 'EUR', 'INR', 'MYR', 'KZT', 'SGD', 'AED', 'CNY', 'JPY'];

const FxRatesPage = () => {
  const base = getBooksTenant()?.base_currency;
  const [rates, setRates] = useState([]);
  const [revals, setRevals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [openReval, setOpenReval] = useState(false);
  const [fetching, setFetching] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      booksClient.get('/fx'),
      booksClient.get('/fx/revaluations'),
    ]).then(([r, v]) => {
      setRates(r.data.data || []);
      setRevals(v.data.data || []);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const fetchAuto = async () => {
    setFetching(true);
    try {
      const r = await booksClient.post('/fx/fetch', {});
      toast({ title: `Fetched ${r.data.inserted} new rates for ${r.data.as_of}` });
      load();
    } catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); }
    finally { setFetching(false); }
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="text-xs font-semibold tracking-[0.3em] text-[#0f5c3d] uppercase">Multi-currency</div>
          <h1 className="text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Foreign Exchange</h1>
          <div className="text-sm text-[#0b1a2e]/60 mt-1">Base currency: <span className="font-semibold">{base}</span> · rates auto-pulled from open.er-api.com; manual overrides always win.</div>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchAuto} disabled={fetching} data-testid="fx-fetch-btn"
            className="inline-flex items-center gap-1 px-4 py-2 border border-[#0f5c3d] text-[#0f5c3d] rounded-full text-sm font-semibold disabled:opacity-50">
            {fetching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DownloadCloud className="w-3.5 h-3.5" />} Fetch today
          </button>
          <button onClick={() => setOpenReval(true)} data-testid="fx-reval-btn"
            className="inline-flex items-center gap-1 px-4 py-2 border border-amber-600 text-amber-700 rounded-full text-sm font-semibold">
            <RefreshCw className="w-3.5 h-3.5" /> Month-end Revaluation
          </button>
          <button onClick={() => setOpenNew(true)} data-testid="fx-new-btn"
            className="inline-flex items-center gap-1 px-4 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold">
            <Plus className="w-3.5 h-3.5" /> Manual Rate
          </button>
        </div>
      </div>

      {loading ? <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> :
        rates.length === 0 ? (
          <div className="p-12 text-center text-[#0b1a2e]/40 italic bg-white rounded-2xl border border-black/5">
            No rates yet. Click <em>Fetch today</em> to pull the latest, or add a manual rate.
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-black/5 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
                <tr>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Pair</th>
                  <th className="text-right px-4 py-2">Rate</th>
                  <th className="text-left px-4 py-2">Source</th>
                </tr>
              </thead>
              <tbody>
                {rates.map((r) => (
                  <tr key={r.rate_id} className="border-t border-slate-100 hover:bg-[#f7fbf8]">
                    <td className="px-4 py-2 text-xs text-[#0b1a2e]/70">{r.date}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.from_currency} <ArrowRight className="w-3 h-3 inline mx-1" /> {r.to_currency}</td>
                    <td className="px-4 py-2 text-right font-mono">{Number(r.rate).toLocaleString(undefined, { maximumFractionDigits: 8 })}</td>
                    <td className="px-4 py-2">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${r.source === 'manual' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>{r.source}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {revals.length > 0 && (
        <div>
          <h2 className="text-xl mb-3 text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Past Revaluations</h2>
          <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
                <tr>
                  <th className="text-left px-4 py-2">As of</th>
                  <th className="text-left px-4 py-2">Journal</th>
                  <th className="text-right px-4 py-2">Net Gain / (Loss)</th>
                  <th className="text-right px-4 py-2">Docs</th>
                </tr>
              </thead>
              <tbody>
                {revals.map((r) => (
                  <tr key={r.reval_id} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-xs">{r.as_of}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.journal_no || '—'}</td>
                    <td className={`px-4 py-2 text-right font-mono ${r.net_gain_loss_base >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {Number(r.net_gain_loss_base).toLocaleString(undefined, { minimumFractionDigits: 2 })} {base}
                    </td>
                    <td className="px-4 py-2 text-right">{(r.documents || []).length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {openNew && <ManualRateDialog base={base} onClose={() => setOpenNew(false)} onSaved={() => { setOpenNew(false); load(); }} />}
      {openReval && <RevalDialog base={base} onClose={() => setOpenReval(false)} onSaved={() => { setOpenReval(false); load(); }} />}
    </div>
  );
};

const ManualRateDialog = ({ base, onClose, onSaved }) => {
  const [form, setForm] = useState({ from_currency: 'USD', to_currency: base, rate: '', date: new Date().toISOString().slice(0, 10) });
  const submit = async () => {
    try {
      await booksClient.post('/fx', { ...form, rate: parseFloat(form.rate) });
      toast({ title: 'Manual rate saved' });
      onSaved();
    } catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Add Manual FX Rate</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <L label="From"><select value={form.from_currency} onChange={(e) => setForm((f) => ({ ...f, from_currency: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm">{CCY_LIST.map((c) => <option key={c} value={c}>{c}</option>)}</select></L>
            <L label="To"><select value={form.to_currency} onChange={(e) => setForm((f) => ({ ...f, to_currency: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm">{CCY_LIST.map((c) => <option key={c} value={c}>{c}</option>)}</select></L>
          </div>
          <L label={`Rate — 1 ${form.from_currency} = ? ${form.to_currency}`}><input type="number" step="0.00000001" value={form.rate} onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))} data-testid="fx-rate-input" className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono" /></L>
          <L label="Date"><input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></L>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#0b1a2e]/70">Cancel</button>
          <button onClick={submit} disabled={!form.rate || form.from_currency === form.to_currency} data-testid="fx-save-btn"
            className="px-5 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold disabled:opacity-50">Save</button>
        </div>
      </div>
    </div>
  );
};

const RevalDialog = ({ base, onClose, onSaved }) => {
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const run = async () => {
    setRunning(true);
    try {
      const r = await booksClient.post('/fx/revalue', null, { params: { as_of: asOf } });
      setResult(r.data);
      toast({ title: r.data.journal_id ? `Revaluation posted (${r.data.journal_no})` : 'Nothing to revalue' });
    } catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); }
    finally { setRunning(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Month-end FX Revaluation</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-[#0b1a2e]/60 mb-3">
          Restates every open AR / AP document in a non-{base} currency at the <em>as-of</em> rate.
          The net gain/(loss) is posted as an unrealised FX journal.
        </p>
        <L label="As-of date"><input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="fx-reval-date" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></L>
        {result && (
          <div className="mt-4 bg-[#f7fbf8] p-3 rounded text-sm space-y-1 border border-[#0f5c3d]/10">
            <div>Docs revalued: <strong>{result.revalued_documents}</strong></div>
            <div>Net gain / (loss): <strong className={result.net_gain_loss_base >= 0 ? 'text-emerald-700' : 'text-red-700'}>{Number(result.net_gain_loss_base).toLocaleString(undefined, { minimumFractionDigits: 2 })} {base}</strong></div>
            {result.journal_no && <div>Journal: <strong>{result.journal_no}</strong></div>}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={() => { onSaved(); }} className="px-4 py-2 text-sm text-[#0b1a2e]/70">Close</button>
          <button onClick={run} disabled={running} data-testid="fx-run-reval-btn"
            className="px-5 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold disabled:opacity-50">
            {running ? 'Running…' : 'Run Revaluation'}
          </button>
        </div>
      </div>
    </div>
  );
};

const L = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0b1a2e]/60 mb-1">{label}</div>
    {children}
  </label>
);

export default FxRatesPage;
